/**
 * Cluster GEO-DATA Fase D — AddressItalyFields
 *
 * Pacchetto condiviso "indirizzo guidato Italia" che applica la stessa
 * UX del form anagrafiche (Fase C) ad altri form geografici:
 *   - Via (Input libero)
 *   - Comune (ComunePicker, ricerca server-side, filtrato per provincia)
 *   - CAP (CapPicker ibrido: dropdown dai CAP del comune + sblocco manuale)
 *   - Provincia (ProvinciaPicker, auto-bind dal comune ma sempre editabile)
 *
 * Le aziende e le sedi nel modello attuale (`AddressIn`) NON hanno il campo
 * `nazione` → si assume Italia (decisione esplicita Fase D). Se in futuro
 * verrà aggiunto il campo nazione al backend, basterà incapsulare questo
 * componente sotto un `NazionePicker` condizionale.
 *
 * Auto-bind:
 *   - Selezione di un Comune → set automatico provincia (se presente) e
 *     reset CAP se quello corrente non è tra i CAP del nuovo comune.
 *   - Il ProvinciaPicker resta sempre editabile (correzioni in casi limite).
 *   - Il CAP è sempre sbloccabile manualmente (CapPicker prop `toggle-manual`).
 *
 * Props:
 *   value          (object) {via, cap, comune, provincia}
 *   onChange       (fn)     (newAddress) → patch completo del campo
 *   testIdPrefix   (string) prefisso per data-testid
 *   showVia        (bool)   default true. Se false nasconde la riga Via.
 *   compact        (bool)   layout 4 colonne stretto (per modal dialog)
 */
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ProvinciaPicker from "@/components/ProvinciaPicker";
import ComunePicker from "@/components/ComunePicker";
import CapPicker from "@/components/CapPicker";

export default function AddressItalyFields({
  value, onChange, testIdPrefix = "addr", showVia = true, compact = false,
}) {
  const addr = value || {};
  const [comuneObj, setComuneObj] = useState(null);

  const set = (patch) => onChange?.({ ...addr, ...patch });

  const onComuneChange = (nome, comune) => {
    setComuneObj(comune || null);
    const patch = { comune: nome || "" };
    if (comune?.provincia_sigla) {
      patch.provincia = comune.provincia_sigla;
    }
    if (comune && addr.cap && !(comune.cap || []).includes(addr.cap)) {
      patch.cap = "";
    }
    set(patch);
  };

  // Layout: in compact mode mettiamo via su tutta la riga, sotto Comune+CAP+Prov.
  // In modalità default invece tutto in una griglia 4 colonne come l'attuale form.
  return (
    <div className={compact ? "grid grid-cols-1 sm:grid-cols-12 gap-2" : "grid grid-cols-2 sm:grid-cols-12 gap-2"}>
      {showVia && (
        <div className={compact ? "sm:col-span-12" : "sm:col-span-12"}>
          <Label className="text-xs">Via</Label>
          <Input
            placeholder="Via, civico…"
            value={addr.via || ""}
            onChange={(e) => set({ via: e.target.value })}
            data-testid={`${testIdPrefix}-via`}
          />
        </div>
      )}
      <div className="sm:col-span-6">
        <Label className="text-xs">Comune</Label>
        <ComunePicker
          value={addr.comune || ""}
          provinciaFilter={addr.provincia || ""}
          onChange={onComuneChange}
          testId={`${testIdPrefix}-comune`}
        />
      </div>
      <div className="sm:col-span-3">
        <Label className="text-xs">CAP</Label>
        <CapPicker
          value={addr.cap || ""}
          comune={comuneObj}
          onChange={(v) => set({ cap: v })}
          testId={`${testIdPrefix}-cap`}
        />
      </div>
      <div className="sm:col-span-3">
        <Label className="text-xs">Prov.</Label>
        <ProvinciaPicker
          value={addr.provincia || ""}
          onChange={(v) => set({ provincia: v || "" })}
          testId={`${testIdPrefix}-provincia`}
        />
      </div>
    </div>
  );
}
